/**
 * Physics World — Global singleton engine.
 * All section-physics-world sections share one Matter.js instance.
 * Elements collide with each other and walls. Idle wiggle for gentle motion.
 * Overlay uses pointer-events:none so CTAs underneath stay clickable.
 */
(function () {
    'use strict';

    // ─── Singleton ──────────────────────────────────────────────
    if (window.__physicsWorld) return;

    var PW = (window.__physicsWorld = {
        engine: null,
        runner: null,
        overlay: null,
        bodies: [],
        domElements: new Map(),
        elementSizes: new Map(),
        animFrameId: 0,
        activeConstraint: null,
        sectionsRegistered: 0,
        config: null,
        booted: false
    });

    // ─── Helpers ────────────────────────────────────────────────
    function isMobile() { return window.innerWidth < 768; }

    // Get true document-level top by temporarily scrolling to 0
    // (avoids all issues with sticky sections distorting offsets)
    // Runs synchronously — browser never renders the intermediate state
    function getDocumentTop(el) {
        var savedScroll = window.scrollY;
        window.scrollTo(0, 0);
        var top = el.getBoundingClientRect().top;   // scrollY=0, so this IS the doc position
        window.scrollTo(0, savedScroll);
        return top;
    }

    // ─── Create Overlay ─────────────────────────────────────────
    function createOverlay() {
        if (PW.overlay) return PW.overlay;
        var el = document.createElement('div');
        el.id = 'physics-overlay';
        el.style.cssText =
            'position:absolute;top:0;left:0;width:100%;' +
            'pointer-events:none;z-index:50;overflow:visible;';
        document.body.appendChild(el);
        PW.overlay = el;
        updateOverlayHeight();
        return el;
    }

    function updateOverlayHeight() {
        if (!PW.overlay) return;
        var h = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight
        );
        PW.overlay.style.height = h + 'px';
    }

    // ─── Register Elements from a Section ───────────────────────
    function registerSection(dataScript) {
        var Engine = Matter.Engine;
        var Bodies = Matter.Bodies;
        var Composite = Matter.Composite;
        var Body = Matter.Body;

        var parsed;
        try { parsed = JSON.parse(dataScript.textContent); }
        catch (e) { console.error('Physics World: bad JSON', e); return; }

        var config = parsed.config || {};
        var elements = parsed.elements || [];
        if (!elements.length) return;

        // First section sets global config
        if (!PW.config) {
            PW.config = {
                enableWiggle: config.enableWiggle !== false,
                wiggleIntensity: config.wiggleIntensity || 3
            };
        }

        // Ensure engine exists (zero gravity — elements float)
        if (!PW.engine) {
            PW.engine = Engine.create();
            PW.engine.world.gravity.y = 0;
            PW.engine.world.gravity.x = 0;
        }

        // Ensure overlay exists
        createOverlay();

        // ── Position reference ──
        // The physics section has zero height (config-only).
        // Each element references another section (prev, next, or custom selector).
        // posY is a % of that reference section's height, offset from its top.
        var sectionWrapper = dataScript.closest('.shopify-section');
        var mobile = isMobile();
        var vpWidth = window.innerWidth;

        elements.forEach(function (el) {
            var size = mobile ? (el.sizeMobile || el.size) : el.size;
            var posX = mobile ? (el.posXMobile != null ? el.posXMobile : el.posX) : el.posX;
            var posY = mobile ? (el.posYMobile != null ? el.posYMobile : el.posY) : el.posY;

            // ── Resolve reference section ──
            var refEl = null;
            var refMode = el.refSection || 'prev';
            if (refMode === 'prev' && sectionWrapper) {
                refEl = sectionWrapper.previousElementSibling;
            } else if (refMode === 'next' && sectionWrapper) {
                refEl = sectionWrapper.nextElementSibling;
            } else if (refMode === 'custom' && el.refSelector) {
                refEl = document.querySelector(el.refSelector);
            }

            // Fallback: if no reference found, use previous sibling or self
            if (!refEl) refEl = (sectionWrapper && sectionWrapper.previousElementSibling) || sectionWrapper;

            // Get true document-level Y by neutralising sticky sections
            var refTop = refEl ? getDocumentTop(refEl) : 0;
            var refHeight = refEl ? refEl.offsetHeight : window.innerHeight;

            // X = % of viewport width
            var xPx = (posX / 100) * vpWidth;
            // Y = reference section top + (posY% of reference section height)
            var yPx = refTop + (posY / 100) * refHeight;

            // Physics body options — hardcoded for clean collisions
            var bodyOpts = {
                label: el.id,
                restitution: 0.6,     // moderate bounce
                frictionAir: 0.04,    // gentle air drag
                friction: 0.1,
                density: 0.001
            };

            var body;
            var shape = el.shape || 'circle';
            if (shape === 'circle') {
                body = Bodies.circle(xPx, yPx, size / 2, bodyOpts);
            } else if (shape === 'pill') {
                body = Bodies.rectangle(xPx, yPx, size * 1.5, size * 0.8, {
                    label: bodyOpts.label,
                    restitution: bodyOpts.restitution,
                    frictionAir: bodyOpts.frictionAir,
                    friction: bodyOpts.friction,
                    density: bodyOpts.density,
                    chamfer: { radius: size * 0.4 }
                });
            } else {
                body = Bodies.rectangle(xPx, yPx, size, size, bodyOpts);
            }

            if (el.rotation) {
                Body.setAngle(body, (el.rotation * Math.PI) / 180);
            }

            // ── DOM element ──
            var div = document.createElement('div');
            div.className = 'pw-element';
            var h = shape === 'pill' ? size * 0.8 : size;
            var w = shape === 'pill' ? size * 1.5 : size;
            div.style.cssText =
                'position:absolute;left:0;top:0;' +
                'width:' + w + 'px;height:' + h + 'px;' +
                'pointer-events:auto;touch-action:none;cursor:grab;' +
                'will-change:transform;';

            if (el.imageUrl) {
                var img = document.createElement('img');
                img.src = el.imageUrl;
                img.alt = el.label || 'Physics element';
                img.draggable = false;
                img.style.cssText =
                    'width:100%;height:100%;object-fit:contain;' +
                    'pointer-events:none;user-select:none;';
                div.appendChild(img);
            } else {
                buildFallbackSVG(div, shape, el.color || '#FF5733');
            }

            PW.overlay.appendChild(div);
            PW.domElements.set(el.id, div);
            PW.elementSizes.set(el.id, { halfW: w / 2, halfH: h / 2 });
            PW.bodies.push(body);

            // Attach drag events
            attachInteraction(div, el.id);
        });

        Composite.add(PW.engine.world, PW.bodies.slice(-elements.length));
        PW.sectionsRegistered++;
    }

    // ─── Fallback SVG ───────────────────────────────────────────
    function buildFallbackSVG(div, shape, color) {
        var ns = 'http://www.w3.org/2000/svg';
        var svg = document.createElementNS(ns, 'svg');
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
        svg.setAttribute('viewBox', '0 0 100 100');
        svg.style.overflow = 'visible';
        svg.style.pointerEvents = 'none';
        var s;
        if (shape === 'circle') {
            s = document.createElementNS(ns, 'circle');
            s.setAttribute('cx', '50'); s.setAttribute('cy', '50');
            s.setAttribute('r', '48'); s.setAttribute('fill', color);
        } else if (shape === 'pill') {
            s = document.createElementNS(ns, 'rect');
            s.setAttribute('x', '0'); s.setAttribute('y', '15');
            s.setAttribute('width', '140'); s.setAttribute('height', '70');
            s.setAttribute('rx', '35'); s.setAttribute('fill', color);
        } else {
            s = document.createElementNS(ns, 'rect');
            s.setAttribute('x', '2'); s.setAttribute('y', '2');
            s.setAttribute('width', '96'); s.setAttribute('height', '96');
            s.setAttribute('rx', '12'); s.setAttribute('fill', color);
        }
        svg.appendChild(s);
        div.appendChild(svg);
    }

    // ─── Interaction (drag) ─────────────────────────────────────
    function attachInteraction(div, id) {
        div.addEventListener('mousedown', function (e) {
            e.preventDefault();
            startDrag(id, e.pageX, e.pageY);
            div.style.cursor = 'grabbing';

            function onMove(me) { moveDrag(me.pageX, me.pageY); }
            function onUp() {
                endDrag();
                div.style.cursor = 'grab';
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
            }
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        });

        div.addEventListener('touchstart', function (e) {
            var t = e.touches[0];
            startDrag(id, t.pageX, t.pageY);

            function onTouchMove(te) {
                if (te.cancelable) te.preventDefault();
                moveDrag(te.touches[0].pageX, te.touches[0].pageY);
            }
            function onTouchEnd() {
                endDrag();
                window.removeEventListener('touchmove', onTouchMove);
                window.removeEventListener('touchend', onTouchEnd);
            }
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
        }, { passive: true });
    }

    function startDrag(id, pageX, pageY) {
        var body = PW.bodies.find(function (b) { return b.label === id; });
        if (!body) return;
        PW.activeConstraint = Matter.Constraint.create({
            bodyA: body,
            pointB: { x: pageX, y: pageY },
            stiffness: 0.2,
            damping: 0.1,
            length: 0
        });
        Matter.Composite.add(PW.engine.world, PW.activeConstraint);
    }

    function moveDrag(pageX, pageY) {
        if (PW.activeConstraint) {
            PW.activeConstraint.pointB = { x: pageX, y: pageY };
        }
    }

    function endDrag() {
        if (PW.activeConstraint) {
            Matter.Composite.remove(PW.engine.world, PW.activeConstraint);
            PW.activeConstraint = null;
        }
    }

    // ─── Walls at document edges ────────────────────────────────
    function createWalls() {
        var Bodies = Matter.Bodies;
        var Composite = Matter.Composite;
        var w = window.innerWidth;
        var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        var t = 1000;

        var wallOpts = { isStatic: true, restitution: 0.6 };
        var walls = [
            Bodies.rectangle(w / 2, -t / 2, w + t * 2, t, wallOpts),           // top
            Bodies.rectangle(w / 2, h + t / 2, w + t * 2, t, wallOpts),         // bottom
            Bodies.rectangle(-t / 2, h / 2, t, h + t * 2, wallOpts),            // left
            Bodies.rectangle(w + t / 2, h / 2, t, h + t * 2, wallOpts)          // right
        ];
        Composite.add(PW.engine.world, walls);
    }

    // ─── Idle Wiggle ────────────────────────────────────────────
    function setupWiggle() {
        var intensity = PW.config.wiggleIntensity || 3;
        // Scale: intensity 1 = very gentle, 10 = chaotic
        var forceMult = intensity * 0.000005;

        Matter.Events.on(PW.engine, 'beforeUpdate', function () {
            PW.bodies.forEach(function (body) {
                // Only wiggle when nearly still
                if (body.speed < 0.3 && body.angularSpeed < 0.03) {
                    var time = PW.engine.timing.timestamp;
                    // Phase offset from body id so they don't move in sync
                    var phase = 0;
                    var lbl = body.label;
                    for (var i = 0; i < lbl.length; i++) phase += lbl.charCodeAt(i);
                    var fx = Math.sin(time * 0.0012 + phase) * forceMult * body.mass;
                    var fy = Math.cos(time * 0.0015 + phase * 1.3) * forceMult * body.mass;
                    Matter.Body.applyForce(body, body.position, { x: fx, y: fy });
                }
            });
        });
    }

    // ─── Render Loop ────────────────────────────────────────────
    function render() {
        PW.bodies.forEach(function (body) {
            var el = PW.domElements.get(body.label);
            var sz = PW.elementSizes.get(body.label);
            if (el && sz) {
                el.style.transform =
                    'translate3d(' + (body.position.x - sz.halfW) + 'px,' +
                    (body.position.y - sz.halfH) + 'px,0) rotate(' +
                    body.angle + 'rad)';
            }
        });
        PW.animFrameId = requestAnimationFrame(render);
    }

    // ─── Boot ───────────────────────────────────────────────────
    function boot() {
        if (PW.booted) return;

        var scripts = document.querySelectorAll('script[id^="physics-world-data-"]');
        if (!scripts.length || !window.Matter) return;

        scripts.forEach(function (s) { registerSection(s); });
        if (!PW.bodies.length) return;

        createWalls();

        if (PW.config && PW.config.enableWiggle) {
            setupWiggle();
        }

        PW.runner = Matter.Runner.create();
        Matter.Runner.run(PW.runner, PW.engine);
        render();

        // Keep overlay height in sync
        var resizeTimer;
        window.addEventListener('resize', function () {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(updateOverlayHeight, 250);
        });
        setInterval(updateOverlayHeight, 3000);

        PW.booted = true;
    }

    // ─── Init ───────────────────────────────────────────────────
    // Use 'load' event to ensure all images/resources are loaded and
    // layout is fully settled before calculating positions.
    if (document.readyState === 'complete') {
        boot();
    } else {
        window.addEventListener('load', boot);
    }

    // ─── Theme Editor Support ───────────────────────────────────
    if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', function () {
            if (PW.runner) Matter.Runner.stop(PW.runner);
            if (PW.animFrameId) cancelAnimationFrame(PW.animFrameId);
            if (PW.engine) {
                Matter.World.clear(PW.engine.world, false);
                Matter.Engine.clear(PW.engine);
            }
            if (PW.overlay) { PW.overlay.remove(); PW.overlay = null; }
            PW.bodies = [];
            PW.domElements = new Map();
            PW.elementSizes = new Map();
            PW.engine = null;
            PW.runner = null;
            PW.config = null;
            PW.booted = false;
            PW.activeConstraint = null;
            PW.sectionsRegistered = 0;
            boot();
        });
    }
})();
